import type { Action } from 'state/actions';
import { SecondaryMarketCpOrderForm } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import { addOrReplaceKey, patchKey, removeKey } from 'state/utils/collection';

import type { ClientData, ClientState, ClientStateByTabIdMap } from './client.model';
import { initialClientState, initialClientStateByTabIdMap } from './client.model';

function resetSettlements(existing: ClientData | undefined): ClientData | undefined {
  if (existing === undefined) {
    return existing;
  }
  return { ...existing, settlements: [] };
}

export function clientReducer(
  state: ClientStateByTabIdMap = initialClientStateByTabIdMap,
  action: Action,
): ClientStateByTabIdMap {
  switch (action.type) {
    case 'SECONDARY_MARKET_CP_ORDER_FORM_RESET':
      return removeKey(state, action.tabId);

    case 'SECONDARY_MARKET_CP_ORDER_RELOAD_FORM_FETCH_REQUESTED':
    case 'SECONDARY_MARKET_CP_ORDER_INIT_FORM_FETCH_REQUESTED':
      return addOrReplaceKey(state, action.tabId, initialClientState);
    case 'SECONDARY_MARKET_CP_FORM_LINKED_TO_CLIENT_FETCH_REQUESTED':
      return patchKey(state, action.tabId, () => ({
        isLoading: true,
        error: undefined,
      }));
    case 'SECONDARY_MARKET_CP_FORM_LINKED_TO_CLIENT_RECEIVED':
      return patchKey(
        state,
        action.tabId,
        old =>
          ({
            data: { ...old.data, ...(action.data ?? {}) },
            isLoading: false,
            error:
              action.settlementTypeError && action.settlementTypeError.payload === 'Unknown counterpart.'
                ? ({
                    translationKey: 'unknownCounterPart',
                    severity: 'Failure',
                  } as SecondaryMarketFormError<SecondaryMarketCpOrderForm>)
                : undefined,
          }) as Partial<ClientState>,
      );
    case 'SECONDARY_MARKET_CP_ORDER_SETTLEMENT_TYPE_REFERENTIAL_RESET':
      return patchKey(state, action.tabId, old => {
        return {
          data: resetSettlements(old.data),
          isLoading: false,
          error: undefined,
        };
      });
    case 'SECONDARY_MARKET_CP_ORDER_SETTLEMENT_TYPES_FAILED':
      return patchKey(state, action.tabId, old => {
        return {
          data: resetSettlements(old.data),
          isLoading: false,
          error: {
            translationKey: action.errorMessage === 'Unknown counterpart.' ? 'unknownCounterPart' : undefined,
            severity: 'Failure',
          } as SecondaryMarketFormError<SecondaryMarketCpOrderForm>,
        };
      });
    case 'SECONDARY_MARKET_CP_ORDER_RELOAD_FORM_FETCH_FAILED':
    case 'SECONDARY_MARKET_CP_FORM_LINKED_TO_CLIENT_FAILED':
      return patchKey(state, action.tabId, () => ({ error: {}, isLoading: false }));
    case 'SECONDARY_MARKET_POSTEVENT_WORKSPACE_REMOVE_TAB':
    case 'SECONDARY_MARKET_QUOTES_WORKSPACE_REMOVE_TAB':
      if (action.tabType === 'closePriceOrder') {
        return removeKey(state, action.tabId);
      }
      return state;
    default:
      return state;
  }
}
